import { Component, ojs, app, payload, each, state, v } from "modular-openscriptjs";
import {
	monetary,
	pageInfo,
	either,
	formatDateForHumans,
	mergeDetails,
	authenticated,
	sendEvent,
	lastOf,
	cleanDispose,
	scrollToTop,
    getComponent
} from "../../../helpers";
import { PAGES, api, routes } from "../../../declarations";
import { dom } from "../../../fotastart";
import InfiniteScroll from "../../Blocks/InfiniteScroll";
import { Capacitor } from "@capacitor/core";
import { getFirebaseToken } from "../../../firebase";

const h = app("h");
const broker = app("broker");
const $e = app("$e");

ojs(InfiniteScroll);

export default class OrderHistory extends Component {
	loading = state(true);
	page = 1;
	lastPage = false;

	$_mounted(id) {
		let c = getComponent(id);

		if(!c) return;

		c.initPage();
		c.loading.value = false;

		if (Capacitor.isNativePlatform()) {
            getFirebaseToken();
        }
	}

	initPage() {
		this.page = 1;
		this.lastPage = false;
		this.loading.value = true;
	}

	render(...args) {
		pageInfo(PAGES.MY_ORDERS);
		scrollToTop();

		return h.div(
			{ class: "pb-5 pt-3 mb-5 py-md-5" },

			// Header / Title
			h.div(
				{ class: "d-flex align-items-center gap-3 mb-4" },
				h.button(
					{
						class: "btn btn-white border shadow-sm circle p-2 d-flex align-items-center justify-content-center",
						style: "width: 40px; height: 40px;",
						onclick: "history.back()",
					},
					h.i({ class: "bi bi-arrow-left fs-5 text-secondary" })
				),
				h.h2({ class: "fw-bold mb-0 h4" }, "My Orders")
			),

			v(this.loading, (l) => {
				if (l.value) {
					return h.div(
						{ class: "text-center py-5" },
						h.div({ class: "spinner-border text-primary" })
					);
				}

				return h.InfiniteScroll({
					child: h.div({
						class: "row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3 w-100 m-0",
						hook: "orders-list",
						uuid: "orders-list-holder"
					}),
					callback: (entries, observer, marker) => {
						if (entries[0]?.isIntersecting && !this.lastPage) {
							this.fetchOrders(marker);
						}
					},
				});
			}),
			...args
		);
	}

	async fetchOrders(marker) {
		if (this.lastPage) return;

		const holder = dom.get("[hook='orders-list']");
		if (!holder) return;

		const spinner = h.Spinner({
			class: "p-3",
			parent: marker,
			resetParent: true,
		});

		let res = await api.get(
			routes.orders,
			{
				page: this.page,
				interval: 5,
			},
			{ silent: true }
		);

		if (!res) {
			spinner.remove();
			return;
		}

		const ordersList = res.data.orders.data;

		if (this.page === 1 && ordersList.length === 0) {
			h.div(
				{ class: "card mb-3 border-0 shadow-sm rounded-4 w-100" },
				h.div(
					{ class: "card-body text-center py-5" },
					h.span(
						{ class: "text-muted fw-medium" },
						"Place your first order. It will appear here."
					)
				),
				{ parent: holder }
			);
		} else {
			for (let order of ordersList) {
				let id = `order-${order.id}`;
				if (!dom.get(`[order-card-id="${id}"]`, holder)) {
					this.renderOrderCard(order, holder, id);
				}
			}
		}

		spinner.remove();

		if (this.page >= res.data.orders.last_page) {
			this.lastPage = true;
		} else {
			this.page++;
			h.button(
				h.i({
					class: "bi bi-arrow-down-circle me-2 fs-5",
					style: "line-height: 0;",
				}),
				"Load More",
				{
					parent: marker,
					class: "btn btn-outline-primary d-flex align-items-center justify-content-center mx-auto my-4 rounded-pill px-4 fw-semibold",
					listeners: {
						click: () => this.fetchOrders(marker),
					},
				}
			);
		}
	}

	renderOrderCard(order, parent, id) {
		h.div(
			{
				order_card_id: id,
				class: "col",
			},
			h.div(
				{
					class: "card border-0 shadow-sm rounded-4 overflow-hidden h-100",
				},
				h.div(
					{ class: "card-body p-4 d-flex flex-column justify-content-between h-100" },
					h.div(
						{},
						h.div(
							{ class: "d-flex justify-content-between align-items-center mb-3" },
							h.div(
								h.h6({ class: "fw-bold text-dark mb-1" }, `Order #${order.order_code}`),
								h.span({ class: "text-muted small" }, formatDateForHumans(order.created_at))
							),
							h.span(
								{ class: `badge rounded-pill px-3 py-1.5 fs-xsm text-uppercase fw-semibold ${this.getStatusBadgeClass(order.status)}` },
								order.status
							)
						),
						
						// Products List
						either(
							order.type !== "errand",
							() => {
								if (order.type === "delivery_request") {
									return h.div(
										{ class: "mb-4 small text-muted font-monospace" },
										"Pickup ➔ Dropoff"
									);
								}

								return h.div(
									{ class: "d-flex flex-column gap-2 mb-4 border-top border-bottom py-3" },
									each(order.items || order.products || [], (product) => {
										let name = product.name || product.deliverable_name || (product.versioned_buyable?.name) || "Item";
										let qty = product.quantity || 1;
										let price = product.total_paid ?? product.price ?? 0;
										return h.div(
											{ class: "d-flex justify-content-between align-items-start small" },
											h.span({ class: "text-muted text-wrap me-2" }, `${qty}x ${name}`),
											h.span({ class: "fw-semibold text-dark text-nowrap" }, monetary(price, window.userCurrency || "KES"))
										);
									})
								);
							}
						)
					),

					// Total and Actions
					h.div(
						{ class: "d-flex justify-content-between align-items-center flex-md-column flex-wrap gap-3 mt-auto" },
						h.div(
							{ class: "w-100 text-md-start" },
							h.span({ class: "text-muted small d-block" }, "Total Paid"),
							h.span({ class: "fw-bold text-primary fs-5" }, monetary(order.total_paid ?? 0, window.userCurrency || "KES"))
						),
						h.div(
							{ class: "d-flex gap-2 w-100 justify-content-md-start justify-content-end flex-wrap" },
							either(
								!order.review_id && order.status == "completed",
								() => h.a(
									{
										class: "btn btn-outline-warning btn-sm rounded-pill px-3 fw-bold d-flex align-items-center gap-1",
										onclick: `app('router').to('/orders/${order.id}/track')`,
									},
									h.i({ class: "fa-solid fa-star" }),
									"Rate"
								)
							),
							h.a(
								{
									class: "btn btn-outline-success btn-sm rounded-pill px-3 fw-bold d-flex align-items-center gap-1 border-0",
									onclick: `app('router').to('/orders/${order.id}/track')`,
								},
								h.i({ class: "fa-solid fa-eye" }),
								"View"
							),
							either(
								order.is_repeatable,
								() => h.a(
									{
										class: "btn btn-outline-primary btn-sm rounded-pill px-3 fw-bold d-flex align-items-center gap-1 border-0",
										onclick: this.method("repeatOrder", order),
									},
									h.i({ class: "fa-solid fa-repeat" }),
									"Repeat"
								)
							)
						)
					)
				)
			),
			{ parent }
		);
	}

	getStatusBadgeClass(status) {
		switch (status?.toLowerCase()) {
			case "completed":
				return "bg-success-subtle text-success border border-success-subtle";
			case "pending":
				return "bg-warning-subtle text-warning-emphasis border border-warning-subtle";
			case "processing":
				return "bg-info-subtle text-info-emphasis border border-info-subtle";
			case "ready":
				return "bg-primary-subtle text-primary border border-primary-subtle";
			case "failed":
			case "cancelled":
				return "bg-danger-subtle text-danger border border-danger-subtle";
			default:
				return "bg-light text-secondary border";
		}
	}

	repeatOrder(order) {
		if (!authenticated()) sendEvent($e.user.needs.authentication);

		if (order.type == "errand") {
			const userId = order.user_id;
			const product = lastOf(order.items)?.product;

			product.original_product_id = product.id;

			return broker.broadcast(
				$e.errand.needs.creation,
				payload({ item: product, userId })
			);
		}

		broker.broadcast(
			$e.order.needs.repeat,
			payload({ orderId: order?.id })
		);
	}
}

ojs(History);
